import { createRouter, createWebHistory } from 'vue-router'
import trade from '@/view/trade/index.js'
import futures from '@/view/futures/index.js'
import mail from '@/view/mail/index.js'
import home from '@/view/home/index.js'
import financial from '@/view/financial/index.js'
import ieo from '@/view/ieo/index.js'
import ipo from '@/view/ipo/index.js'
import quotes from '@/view/quotes/index.js'
import user from '@/view/user/index.js'
import help from '@/view/help/index.js'
import rebate from '@/view/rebate/index.js'
import activity from '@/view/activity/index.js'
import wallet from '@/view/wallet/index.js'
import psersonalCenter from '@/view/psersonalCenter/index.js'
import c2c from '@/view/c2c/index.js'
import AppFooter from '@/layout/appFooter/AppFooter.vue'
import TableModel from '@/components/table/TableModel.vue'
import channelVerify from '@/view/channelVerify/index.js'
import vote from '@/view/vote/index.js'
import agent from '@/view/agent/index.js'
import document from '@/view/documents/index.js'
import proofOfReserves from '@/view/proofOfReserves/index.js'
import lawEnforcement from '@/view/lawEnforcement/index.js'
import stock from '@/view/stock/index.js'
import bifund from '@/view/bifund/index.js'
import {
  DEFAULT_HOME_PATH,
  SUPPORTED_LOCALES,
  canonicalizeLocaleTarget,
  getCurrentLocale,
  localizeRouteRecord,
} from '@/utils/localeRoute.js'
import { updateRuntimeSeoUrl } from '@/utils/seo/runtimeSeoUrl'

const routerHistory = createWebHistory()

const baseRoutes = [
  { path: '/', redirect: DEFAULT_HOME_PATH },
  ...home.routes,
  ...financial.routes,
  ...quotes.routes,
  ...ieo.routes,
  ...ipo.routes,
  ...trade.routes,
  ...futures.routes,
  ...mail.routes,
  ...user.routes,
  ...help.routes,
  ...rebate.routes,
  ...activity.routes,
  ...wallet.routes,
  ...psersonalCenter.routes,
  ...c2c.routes,
  ...channelVerify.routes,
  ...vote.routes,
  ...agent.routes,
  ...document.routes,
  ...proofOfReserves.routes,
  ...lawEnforcement.routes,
  ...stock.routes,
  ...bifund.routes,
  {
    path: '/footer',
    component: AppFooter,
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404',
  },
  {
    path: '/TableModel',
    component: TableModel,
  },
]

const localizedRoutes = baseRoutes
  .filter(route => route.path !== '/')
  .map(localizeRouteRecord)

const routes = [
  ...baseRoutes,
  {
    path: `/:locale(${SUPPORTED_LOCALES.join('|')})`,
    redirect: to => ({
      path: `/${to.params.locale}${DEFAULT_HOME_PATH}`,
      query: to.query,
      hash: to.hash,
      replace: true,
    })
  },
  ...localizedRoutes,
]

const router = createRouter({
  history: routerHistory,
  routes
})

/**
 * 获取导航过程中语言码的回退值
 * 优先级：当前路由路径 > 本地存储 > 浏览器默认语言
 * @returns {string} 确定的语言码
 * @example 当前路径为 '/en_us/home' -> 返回 'en_us'
 */
const getNavigationFallbackLocale = () => {
  return getCurrentLocale({
    path: router.currentRoute.value?.path,
  })
}

/**
 * 规范化导航目标对象，确保其包含正确的语言码前缀
 * @param {string|Object} target - 原始导航目标（路径字符串或路由对象）
 * @returns {Object} 规范化后的导航目标对象
 * @example '/home' -> { path: '/en_us/home', query: {}, hash: '' }
 */
const normalizeNavigationTarget = (target) => {
  if (!target) {
    return target
  }

  const resolved = router.resolve(target)
  const canonicalTarget = canonicalizeLocaleTarget({
    path: resolved.path,
    query: resolved.query,
    hash: resolved.hash,
    fallbackLocale: getNavigationFallbackLocale(),
  })

  if (typeof target === 'string') {
    return {
      path: canonicalTarget.path,
      query: canonicalTarget.query,
      hash: canonicalTarget.hash,
    }
  }

  const nextTarget = {
    ...target,
    path: canonicalTarget.path,
    query: canonicalTarget.query,
    hash: canonicalTarget.hash,
  }

  if (nextTarget.name) {
    delete nextTarget.name
    delete nextTarget.params
  }

  return nextTarget
}

const rawPush = router.push.bind(router)
const rawReplace = router.replace.bind(router)

router.push = (target) => rawPush(normalizeNavigationTarget(target))
router.replace = (target) => rawReplace(normalizeNavigationTarget(target))

router.onError((error, to) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('error loading dynamically imported module')
  ) {
    if (!to?.fullPath) {
      window.location.reload()
    } else {
      window.location = to.fullPath
    }
  }
}
)

router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore()
  const fallbackLocale = getCurrentLocale({
    path: router.currentRoute.value?.path,
  })
  const canonicalTarget = canonicalizeLocaleTarget({
    path: to.path,
    query: to.query,
    hash: to.hash,
    fallbackLocale,
  })

  if (canonicalTarget.path !== to.path || canonicalTarget.changed) {
    next({
      path: canonicalTarget.path,
      query: canonicalTarget.query,
      hash: canonicalTarget.hash,
      replace: true,
    })
    return
  }

  if (to.matched.some(record => record.meta?.requireLogin) && !userStore.token) {
    next({
      path: '/login',
      query: {
        redirect: to.fullPath,
      },
    })
    return
  }

  next()
})

router.afterEach(() => {
  updateRuntimeSeoUrl()
})

export default router
